import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

/**
 * @param {string} url - API endpoint
 * @param {string|number} pageId - unique cache key
 */
const useCachedData = (url, pageId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const prevDataRef = useRef(null);
  const storageKey = `cachedData_${pageId}`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const cached = localStorage.getItem(storageKey);
        if (cached) {
          setData(JSON.parse(cached));
          setLoading(false);
        }

        const response = await axios.get(url);
        const freshData = response?.data?.data ?? [];
        setData(freshData);
        setLoading(false);

        if (freshData.length > 0) {
          try {
            localStorage.setItem(storageKey, JSON.stringify(freshData));
          } catch (e) {
            if (e.name === 'QuotaExceededError') {
              console.warn('LocalStorage quota exceeded; clearing some data.');
              localStorage.removeItem(storageKey);
            } else {
              console.error('Error writing to localStorage:', e);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [url, storageKey]);

  useEffect(() => {
    if (prevDataRef.current !== data && data) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(data));
        prevDataRef.current = data;
      } catch (e) {
        if (e.name === 'QuotaExceededError') {
          console.warn('Quota exceeded while updating cache.');
        }
      }
    }
  }, [data, storageKey]);

  return { data, loading };
};

export default useCachedData;
